import { NewPackageForm } from '@/components/dashboard/signatures/NewPackageForm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { PackageDetails, PackageType, useSignatureQueries } from '@/hooks/queries/useSignatureQueries';
import { useAuthWithProfile } from '@/hooks/useAuth';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FilePen, FilePlus, User } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/_protected/dashboard/signatures/')({
  component: SignaturesDashboard,
});


function PackageList({ packages, onSelect }: { packages: PackageDetails[], onSelect: (pkg: PackageDetails) => void }) {
  const getStatusBadge = (status: PackageDetails['status']) => {
    switch (status) {
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      case 'sent':
        return <Badge variant="default">Sent</Badge>;
      case 'completed':
        return <Badge variant="default" className="bg-green-500 hover:bg-green-600">Completed</Badge>;
      case 'expired':
        return <Badge variant="destructive">Expired</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Cancelled</Badge>;
    }
  };


  return (
    <div className="space-y-4">
      {packages.length === 0 ? (
        <Card className="p-8 text-center">
          <CardContent className="pt-6 flex flex-col items-center justify-center">
            <FilePen className="h-12 w-12 text-muted-foreground mb-4" />
            <p>No signature packages yet.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Create your first package to get started.
            </p>
          </CardContent>
        </Card>
      ) : (
        packages.map(pkg => (
          <Card
            key={pkg.package_id}
            className="hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => onSelect(pkg)}
          >
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <CardTitle className="text-lg">{pkg.name}</CardTitle>
                {getStatusBadge(pkg.status)}
              </div>
              <CardDescription>
                Created {new Date(pkg.created_at! as string).toLocaleDateString()}
              </CardDescription>
            </CardHeader>
            <CardContent className="pb-4">
              <div className="flex items-center text-sm text-muted-foreground">
                {pkg.signers && pkg.signers.length > 0 ? (
                  <>
                    <User className="h-4 w-4 mr-1" />
                    <span>
                      {pkg.signers.length === 1
                        ? (pkg.signers[0].name || pkg.signers[0].email)
                        : `${pkg.signers.length} signers`}
                    </span>
                  </>
                ) : (
                  <>
                    <User className="h-4 w-4 mr-1" />
                    <span>No signers assigned</span>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

function SignaturesDashboard() {
  const { profile } = useAuthWithProfile();
  const navigate = useNavigate();
  const [openCreatePackage, setOpenCreatePackage] = useState(false);
  const { useFetchPackages, createPackageMutation } = useSignatureQueries();
  const { data: packages } = useFetchPackages(profile.user_id);

  const handleSelectPackage = (pkg: PackageType) => {
    navigate({ to: '/dashboard/signatures/$packageId', params: { packageId: pkg.package_id } });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Your Signature Packages</CardTitle>
        <CardDescription>
          Manage your document signature packages. Create new packages, send them for signatures, and track their status.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <PackageList packages={packages} onSelect={handleSelectPackage} />
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={() => setOpenCreatePackage(true)} disabled={createPackageMutation.isPending}>
          <FilePlus className="h-4 w-4 mr-2" />
          {createPackageMutation.isPending ? 'Creating...' : 'Create New Package'}
        </Button>
        <NewPackageForm
          userId={profile.user_id}
          open={openCreatePackage}
          setOpen={setOpenCreatePackage}
          onSuccess={handleSelectPackage}
        />
      </CardFooter>
    </Card>
  );
}
